import { useCallback, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { ResolvedColumn } from '../../domain';

interface ResizeState {
    id: string;
    width: number;
}

export interface ColumnResize<T> {
    resizingId: string | null;
    apply: (columns: ResolvedColumn<T>[]) => ResolvedColumn<T>[];
    start: (
        event: ReactPointerEvent<HTMLElement>,
        col: ResolvedColumn<T>,
    ) => void;
}

function startWidth<T>(handle: HTMLElement, col: ResolvedColumn<T>): number {
    const cell = handle.closest('th');
    const measured = cell ? cell.getBoundingClientRect().width : 0;
    return measured > 0 ? measured : col.width ?? col.minWidth ?? 0;
}

export function useColumnResize<T>(
    onResize?: (columnId: string, width: number) => void,
): ColumnResize<T> {
    const [state, setState] = useState<ResizeState | null>(null);

    const start = useCallback(
        (
            event: ReactPointerEvent<HTMLElement>,
            col: ResolvedColumn<T>,
        ): void => {
            if (!onResize) {
                return;
            }
            event.preventDefault();

            const handle = event.currentTarget;
            const base = startWidth(handle, col);
            const floor = col.minWidth ?? 0;
            const originX = event.clientX;
            let width = Math.max(base, floor);

            setState({ id: col.id, width });

            const move = (moveEvent: PointerEvent): void => {
                width = Math.max(base + moveEvent.clientX - originX, floor);
                setState({ id: col.id, width });
            };

            const stop = (): void => {
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', stop);
                window.removeEventListener('pointercancel', stop);
                setState(null);
                onResize(col.id, Math.round(width));
            };

            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', stop);
            window.addEventListener('pointercancel', stop);
        },
        [onResize],
    );

    const apply = useCallback(
        (columns: ResolvedColumn<T>[]): ResolvedColumn<T>[] => {
            if (!state) {
                return columns;
            }
            return columns.map((col) =>
                col.id === state.id ? { ...col, width: state.width } : col,
            );
        },
        [state],
    );

    return { resizingId: state?.id ?? null, apply, start };
}
